import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { fmtNum, fmtUsd } from "../utils/fmt";
import { useToast } from "../utils/useToast";
import SlotNumber from "../components/SlotNumber";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGear, faXmark } from "@fortawesome/free-solid-svg-icons";


const LME_UNIT_LABEL = {
  per_oz: "per Toz",
  per_g: "per g",
};

const WEIGHT_UNIT_LABEL = {
  g: "g",
  kg: "kg",
};

function applyJump(ppm, jumpPct) {
  const p = numOr0(ppm);
  const j = numOr0(jumpPct);
  return p * (1 + j / 100);
}


function numOr0(v) {
  if (v === "" || v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function numOrNull(v) {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function weightToKg(weight, unit) {
  const w = numOr0(weight);
  if (!w) return 0;
  return unit === "kg" ? w : w / 1000;
}

// normalize different result shapes into a common view model
function normalizeResult({ contractKey, sharedWeightKg, raw }) {
  const r = raw || {};

  // try common fields
  const netUsd = r.netUsd ?? r.netUSD ?? r.usd ?? r.totalUsd ?? null;
  const fxRate = r.fxRate ?? r.fx ?? null;
  const netLocal = r.netLocal ?? r.local ?? null;

  // some calculators return weight in result; if not, use shared
  const weightKg =
    r.weightKg != null
      ? Number(r.weightKg)
      : sharedWeightKg;

  const usdPerKg =
    weightKg > 0 && netUsd != null ? Number(netUsd) / weightKg : null;

  const localPerKg =
    weightKg > 0 && netLocal != null ? Number(netLocal) / weightKg : null;

  // optional extras (safe)
  const ppm = r.ppm || null;

  return {
    contractKey,
    netUsd,
    weightKg,
    usdPerKg,
    fxRate,
    netLocal,
    localPerKg,
    ppm,
    raw: r,
  };
}

export default function ContractCompare({ authed, role }) {
  const { toast, showToast } = useToast();

  // shared inputs
  const [customerName, setCustomerName] = useState("");
  const [weight, setWeight] = useState("");
  const [weightUnit, setWeightUnit] = useState("g");

  const [fxRate, setFxRate] = useState("");

  const [ptPpm, setPtPpm] = useState("");
  const [pdPpm, setPdPpm] = useState("");
  const [rhPpm, setRhPpm] = useState("");

  const [ptJumpPct, setPtJumpPct] = useState("");
  const [pdJumpPct, setPdJumpPct] = useState("");
  const [rhJumpPct, setRhJumpPct] = useState("");

  const [jumpDetach, setJumpDetach] = useState({ pt: false, pd: false, rh: false });
  const [ppmDetach, setPpmDetach] = useState({ pt: false, pd: false, rh: false });

  const [compareSettings, setCompareSettings] = useState(null);
  // shared LME unit for all (keeps UX clean)
  const [lmeUnit, setLmeUnit] = useState("per_oz");

  // per-contract LME
  const [lmeAdmin, setLmeAdmin] = useState({ pt: "", pd: "", rh: "" });
  const [lmeBasf, setLmeBasf] = useState({ pt: "", pd: "", rh: "" });
  const [lmePercent, setLmePercent] = useState({ pt: "", pd: "", rh: "" });

  const [loading, setLoading] = useState(false);

  const [resAdmin, setResAdmin] = useState(null);
  const [resBasf, setResBasf] = useState(null);
  const [resPercent, setResPercent] = useState(null);

  const weightKg = useMemo(() => weightToKg(weight, weightUnit), [weight, weightUnit]);
  const [showCalcUnitModal, setShowCalcUnitModal] = useState(false);
  const [lmeOpen, setLmeOpen] = useState(false);
  const [weightOpen, setWeightOpen] = useState(false);

  const refLmeTrigger = useRef(null);
  const refWeightTrigger = useRef(null);
  const refLmeOptOz = useRef(null);
  const refWeightOptG = useRef(null);

    // % deductions (percent kept) — ONLY for Price Calc (percent)
  const [pctDetach, setPctDetach] = useState({ pt: false, pd: false, rh: false });
  const [ptPct, setPtPct] = useState("");
  const [pdPct, setPdPct] = useState("");
  const [rhPct, setRhPct] = useState("");


  const UNITS_KEY = useMemo(() => {
    return `contractCompare:units:${role || "anon"}`;
  }, [role]);

  function safeParse(json) {
    try { return JSON.parse(json); } catch { return null; }
  }

  function persistUnits(next) {
    try { localStorage.setItem(UNITS_KEY, JSON.stringify(next)); } catch {}
  }

  function readUnits() {
    try { return safeParse(localStorage.getItem(UNITS_KEY) || ""); } catch { return null; }
  }

function num0(v) {
  if (v === "" || v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function focusNext(ref) {
  if (!ref?.current) return;
  ref.current.focus({ preventScroll: true });
  ref.current.select?.();
}

function onSelectTriggerEnter(e, isOpen, setOpen, firstOptionRef) {
  if (e.key !== "Enter" && e.key !== "NumpadEnter") return;

  e.preventDefault();
  e.stopPropagation();

  if (!isOpen) {
    setOpen(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => focusNext(firstOptionRef));
    });
    return;
  }

  focusNext(firstOptionRef);
}

const savedJump = compareSettings?.ppmJumpPct || {};
const savedPtJump = savedJump.pt ?? 0;
const savedPdJump = savedJump.pd ?? 0;
const savedRhJump = savedJump.rh ?? 0;

function jumpDisplay(metal) {
  const det = jumpDetach[metal];
  if (metal === "pt") return det ? ptJumpPct : String(savedPtJump ?? 0);
  if (metal === "pd") return det ? pdJumpPct : String(savedPdJump ?? 0);
  return det ? rhJumpPct : String(savedRhJump ?? 0);
}

function detachJump(metal) {
  setJumpDetach((s) => {
    if (s[metal]) return s;
    // prefill typed state from saved when first detaching
    if (metal === "pt") setPtJumpPct(String(savedPtJump ?? 0));
    if (metal === "pd") setPdJumpPct(String(savedPdJump ?? 0));
    if (metal === "rh") setRhJumpPct(String(savedRhJump ?? 0));
    return { ...s, [metal]: true };
  });
}

const savedPct = compareSettings?.pctKept || {};
const savedPtPct = savedPct.pt ?? 100;
const savedPdPct = savedPct.pd ?? 100;
const savedRhPct = savedPct.rh ?? 100;

function pctDisplay(metal) {
  const det = pctDetach[metal];
  if (metal === "pt") return det ? ptPct : String(savedPtPct);
  if (metal === "pd") return det ? pdPct : String(savedPdPct);
  return det ? rhPct : String(savedRhPct);
}

function detachPct(metal) {
  setPctDetach((s) => {
    if (s[metal]) return s;
    // prefill typed state from saved when first detaching
    if (metal === "pt") setPtPct(String(savedPtPct ?? 100));
    if (metal === "pd") setPdPct(String(savedPdPct ?? 100));
    if (metal === "rh") setRhPct(String(savedRhPct ?? 100));
    return { ...s, [metal]: true };
  });
}

function reattachPct(metal) {
  setPctDetach((s) => ({ ...s, [metal]: false }));
  if (metal === "pt") setPtPct("");
  if (metal === "pd") setPdPct("");
  if (metal === "rh") setRhPct("");
}


function reattachJump(metal) {
  setJumpDetach((s) => ({ ...s, [metal]: false }));
  if (metal === "pt") setPtJumpPct("");
  if (metal === "pd") setPdJumpPct("");
  if (metal === "rh") setRhJumpPct("");
}

// effective jump % = override if detached, else saved settings
function effectiveJumpPct(metal) {
  const s = compareSettings?.ppmJumpPct || {};
  if (metal === "pt") return jumpDetach.pt ? num0(ptJumpPct) : num0(s.pt);
  if (metal === "pd") return jumpDetach.pd ? num0(pdJumpPct) : num0(s.pd);
  return jumpDetach.rh ? num0(rhJumpPct) : num0(s.rh);
}

function effectivePpmValue(enteredPpm, jumpPct) {
  const e = num0(enteredPpm);
  const j = num0(jumpPct);
  return e * (1 + j / 100);
}

const ptJumpEff = effectiveJumpPct("pt");
const pdJumpEff = effectiveJumpPct("pd");
const rhJumpEff = effectiveJumpPct("rh");

const ptPpmEff = effectivePpmValue(ptPpm, ptJumpEff);
const pdPpmEff = effectivePpmValue(pdPpm, pdJumpEff);
const rhPpmEff = effectivePpmValue(rhPpm, rhJumpEff);

function detachPpm(metal) {
  setPpmDetach((s) => ({ ...s, [metal]: true }));
}
function reattachPpm(metal) {
  setPpmDetach((s) => ({ ...s, [metal]: false }));
}

// what we show inside the PPM input (THIS is the Basf behavior)
function ppmDisplay(metal) {
  if (metal === "pt") return ppmDetach.pt ? ptPpm : String(Math.round(ptPpmEff));
  if (metal === "pd") return ppmDetach.pd ? pdPpm : String(Math.round(pdPpmEff));
  return ppmDetach.rh ? rhPpm : String(Math.round(rhPpmEff));
}

function buildSharedInput() {
  const jump = {};
  if (jumpDetach.pt) jump.pt = ptJumpPct === "" ? 0 : Number(ptJumpPct);
  if (jumpDetach.pd) jump.pd = pdJumpPct === "" ? 0 : Number(pdJumpPct);
  if (jumpDetach.rh) jump.rh = rhJumpPct === "" ? 0 : Number(rhJumpPct);

  return {
    weight: numOr0(weight),
    weightUnit,
    ptPpm: numOr0(ptPpm),
    pdPpm: numOr0(pdPpm),
    rhPpm: numOr0(rhPpm),
    lmeUnit,
    fxRate: numOrNull(fxRate),
    ...(Object.keys(jump).length ? { ppmJumpPct: jump } : {}),
  };
}


  function buildInputForLme(lme) {
    const shared = buildSharedInput();
    return {
      ...shared,
      lme: {
        pt: numOr0(lme.pt),
        pd: numOr0(lme.pd),
        rh: numOr0(lme.rh),
      },
    };
  }

  useEffect(() => {
  if (!authed || role !== "admin") return;

  // If you later add backend settings, replace this with api.contractCompareGetSettings()
  setCompareSettings({
    ppmJumpPct: { pt: 0, pd: 0, rh: 0 },
    pctKept: { pt: 100, pd: 100, rh: 100 },
  });

}, [authed, role]);

    useEffect(() => {
    if (!authed || role !== "admin") return;
    const saved = readUnits();
    if (!saved) return;

    if (saved.weightUnit) setWeightUnit(saved.weightUnit);
    if (saved.lmeUnit) setLmeUnit(saved.lmeUnit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, role]);

    useEffect(() => {
    if (!authed || role !== "admin") return;
    persistUnits({ weightUnit, lmeUnit });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, role, weightUnit, lmeUnit]);

  useEffect(() => {
  if (!showCalcUnitModal) {
    setLmeOpen(false);
    setWeightOpen(false);
  }
}, [showCalcUnitModal]);

function buildInputForPercent() {
  const base = buildInputForLme(lmePercent);

  // Only include overrides for metals the user detached (same pattern as jump)
  const deductionPct = {};
  if (pctDetach.pt) deductionPct.pt = ptPct === "" ? 100 : Number(ptPct);
  if (pctDetach.pd) deductionPct.pd = pdPct === "" ? 100 : Number(pdPct);
  if (pctDetach.rh) deductionPct.rh = rhPct === "" ? 100 : Number(rhPct);

  return {
    ...base,

    // ✅ critical: backend defaults to NONE otherwise
    pricingMode: "PERCENT",

    ...(Object.keys(deductionPct).length ? { deductionPct } : {}),
  };
}




  async function runCompare() {
    if (!authed) return showToast("Please login", "err");
    if (role !== "admin") return showToast("Admin only", "err");

    const name = (customerName || "").trim();
    if (!name) return showToast("Customer name is required", "err");
    if (!numOr0(weight)) return showToast("Weight is required", "err");

    setLoading(true);
    setResAdmin(null);
    setResBasf(null);
    setResPercent(null);

    try {
      // Run all 3 in parallel
      const [a, b, p] = await Promise.all([
        api.adminCalcRun({ customerName: name, input: buildInputForLme(lmeAdmin) }),
        api.basfCalcRun({ customerName: name, input: buildInputForLme(lmeBasf) }),
        // percent expects (input, label) based on your api.js
        api.percentCalcRun(buildInputForPercent(), name),
      ]);

      // normalize results (different calculators may return different shapes)
      const aNorm = normalizeResult({
        contractKey: "admin",
        sharedWeightKg: weightKg,
        raw: a?.result ?? a,
      });

      const bNorm = normalizeResult({
        contractKey: "basf",
        sharedWeightKg: weightKg,
        raw: b?.result ?? b,
      });

      const pNorm = normalizeResult({
        contractKey: "percent",
        sharedWeightKg: weightKg,
        raw: p?.result ?? p,
      });

      setResAdmin(aNorm);
      setResBasf(bNorm);
      setResPercent(pNorm);

      showToast("Compared contracts", "ok");
    } catch (e) {
      showToast(e?.message || "Compare failed", "err");
    } finally {
      setLoading(false);
    }
  }

  function ResultCard({ title, model }) {
    const hasLocal = model?.netLocal != null && model?.fxRate != null;

    return (
      <div className="card" style={{ minWidth: 320 }}>
        <div className="cardHeader">
          <h2 style={{ margin: 0 }}>{title}</h2>
          <div className="smallNote">{model ? "Calculated" : "—"}</div>
        </div>

        {!model ? (
          <div className="smallNote">Run compare to see results.</div>
        ) : (
          <div className="appear">
            <div className="result4Head">
              <div className="result4HeadCell">USD</div>
              <div className="result4HeadCell result4HeadRight">Local</div>
            </div>

            <div className="result4Grid">
              {/* total */}
              <div className="resultCell">
                <div className="resultValue">
                  {model.netUsd == null ? (
                    <div className="resultMuted">—</div>
                  ) : (
                    <SlotNumber value={Number(model.netUsd)} decimals={2} prefix="$" suffix="/total" />
                  )}
                </div>
              </div>

              <div className="resultCell resultCellRight">
                {hasLocal ? (
                  <div className="resultValue">
                    <SlotNumber value={Number(model.netLocal)} decimals={2} suffix="/total" />
                  </div>
                ) : (
                  <div className="resultValue resultMuted">—</div>
                )}
              </div>

              {/* per kg */}
              <div className="resultCell">
                <div className="resultValue">
                  {model.usdPerKg == null ? (
                    <div className="resultMuted">—</div>
                  ) : (
                    <SlotNumber value={Number(model.usdPerKg)} decimals={2} prefix="$" suffix="/kg" />
                  )}
                </div>
              </div>

              <div className="resultCell resultCellRight">
                {hasLocal && model.localPerKg != null ? (
                  <div className="resultValue">
                    <SlotNumber value={Number(model.localPerKg)} decimals={2} suffix="/kg" />
                  </div>
                ) : (
                  <div className="resultValue resultMuted">—</div>
                )}
              </div>
            </div>

            <div className="sep" />
            <div className="smallNote">
              Weight: <b>{fmtNum(model.weightKg, 3)} kg</b> • LME unit: <b>{LME_UNIT_LABEL[lmeUnit]}</b>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (!authed) return <div className="card">Please login.</div>;
  if (role !== "admin") return <div className="card">Admin only.</div>;

  return (
    <div className="gridCalc">
      {/* Shared inputs */}
      <div className="card gridFullWidth">
        <div className="cardHeader">
          <h2 style={{ margin: 0 }}>Contract Compare</h2>
          <div className="smallNote">Enter shared inputs once. LME is per contract.</div>
        </div>
                <div className="fieldStack" style={{ justifySelf: "end" }}>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>

                    <button
                    type="button"
                    className="iconBtn"
                    onClick={() => setShowCalcUnitModal(true)}
                    aria-label="Calculator units"
                    title="Calculator units"
                    >
                    <FontAwesomeIcon icon={faGear} />
                    </button>
                </div>
                </div>
        <div className="form">
          <label>Customer name</label>
          <input
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            placeholder="e.g. John Doe"
          />

          <label>
            Weight <span className="smallNote">({WEIGHT_UNIT_LABEL[weightUnit]})</span>
          </label>
          <div className="row3">
            <div className="inputWithSuffix">
              <input
                type="number"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder="Weight"
              />
              <span className="suffix">{weightUnit}</span>
            </div>



          </div>

          <label>USD exchange rate (optional)</label>
          <input
            type="number"
            value={fxRate}
            onChange={(e) => setFxRate(e.target.value)}
            placeholder="If set, shows local price too"
          />

            <label>PPM (entered)</label>
            <div className="row3">
            <div className="inputWithSuffix">
                <input
                type="number"
                value={ppmDisplay("pt")}
                onFocus={() => detachPpm("pt")}
                onBlur={() => reattachPpm("pt")}
                onChange={(e) => {
                    detachPpm("pt");
                    setPtPpm(e.target.value);
                }}
                placeholder="Pt"
                />
                <span className="suffix">pt</span>
            </div>

            <div className="inputWithSuffix">
                <input
                type="number"
                value={ppmDisplay("pd")}
                onFocus={() => detachPpm("pd")}
                onBlur={() => reattachPpm("pd")}
                onChange={(e) => {
                    detachPpm("pd");
                    setPdPpm(e.target.value);
                }}
                placeholder="Pd"
                />
                <span className="suffix">pd</span>
            </div>

            <div className="inputWithSuffix">
                <input
                type="number"
                value={ppmDisplay("rh")}
                onFocus={() => detachPpm("rh")}
                onBlur={() => reattachPpm("rh")}
                onChange={(e) => {
                    detachPpm("rh");
                    setRhPpm(e.target.value);
                }}
                placeholder="Rh"
                />
                <span className="suffix">rh</span>
            </div>
            </div>

            <label>PPM Increase %</label>
            <div className="row3">
            <div className="inputWithAffix">
                <input
                type="number"
                value={jumpDisplay("pt")}
                onFocus={() => detachJump("pt")}
                onChange={(e) => {
                    detachJump("pt");
                    setPtJumpPct(e.target.value);
                }}
                className={jumpDetach.pt ? "isDetached" : ""}
                />
                <button
                type="button"
                className="affixBtn"
                onClick={() => reattachJump("pt")}
                title="Use saved"
                tabIndex={-1}
                >
                ↺
                </button>
                <span className="affixSuffix">pt</span>
            </div>

            <div className="inputWithAffix">
                <input
                type="number"
                value={jumpDisplay("pd")}
                onFocus={() => detachJump("pd")}
                onChange={(e) => {
                    detachJump("pd");
                    setPdJumpPct(e.target.value);
                }}
                className={jumpDetach.pd ? "isDetached" : ""}
                />
                <button
                type="button"
                className="affixBtn"
                onClick={() => reattachJump("pd")}
                title="Use saved"
                tabIndex={-1}
                >
                ↺
                </button>
                <span className="affixSuffix">pd</span>
            </div>

            <div className="inputWithAffix">
                <input
                type="number"
                value={jumpDisplay("rh")}
                onFocus={() => detachJump("rh")}
                onChange={(e) => {
                    detachJump("rh");
                    setRhJumpPct(e.target.value);
                }}
                className={jumpDetach.rh ? "isDetached" : ""}
                />
                <button
                type="button"
                className="affixBtn"
                onClick={() => reattachJump("rh")}
                title="Use saved"
                tabIndex={-1}
                >
                ↺
                </button>
                <span className="affixSuffix">rh</span>
            </div>
            </div>


          <div style={{ marginTop: 10 }}>
            <button type="button" className="btnPrimary" onClick={runCompare} disabled={loading}>
              {loading ? "Comparing…" : "Compare (Run all 3)"}
            </button>
          </div>
        </div>
      </div>

      {/* Per-contract LME */}
      <div className="card">
        <div className="cardHeader">
          <h2 style={{ margin: 0 }}>Tech LME</h2>
          <div className="smallNote">({LME_UNIT_LABEL[lmeUnit]})</div>
        </div>

        <div className="form">
          <label>LME prices</label>
          <div className="row3">
            <div className="inputWithSuffix">
              <input
                type="number"
                value={lmeAdmin.pt}
                onChange={(e) => setLmeAdmin({ ...lmeAdmin, pt: e.target.value })}
                placeholder="Pt"
              />
              <span className="suffix">pt</span>
            </div>
            <div className="inputWithSuffix">
              <input
                type="number"
                value={lmeAdmin.pd}
                onChange={(e) => setLmeAdmin({ ...lmeAdmin, pd: e.target.value })}
                placeholder="Pd"
              />
              <span className="suffix">pd</span>
            </div>
            <div className="inputWithSuffix">
              <input
                type="number"
                value={lmeAdmin.rh}
                onChange={(e) => setLmeAdmin({ ...lmeAdmin, rh: e.target.value })}
                placeholder="Rh"
              />
              <span className="suffix">rh</span>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="cardHeader">
          <h2 style={{ margin: 0 }}>BASF LME</h2>
          <div className="smallNote">({LME_UNIT_LABEL[lmeUnit]})</div>
        </div>

        <div className="form">
          <label>LME prices</label>
          <div className="row3">
            <div className="inputWithSuffix">
              <input
                type="number"
                value={lmeBasf.pt}
                onChange={(e) => setLmeBasf({ ...lmeBasf, pt: e.target.value })}
                placeholder="Pt"
              />
              <span className="suffix">pt</span>
            </div>
            <div className="inputWithSuffix">
              <input
                type="number"
                value={lmeBasf.pd}
                onChange={(e) => setLmeBasf({ ...lmeBasf, pd: e.target.value })}
                placeholder="Pd"
              />
              <span className="suffix">pd</span>
            </div>
            <div className="inputWithSuffix">
              <input
                type="number"
                value={lmeBasf.rh}
                onChange={(e) => setLmeBasf({ ...lmeBasf, rh: e.target.value })}
                placeholder="Rh"
              />
              <span className="suffix">rh</span>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="cardHeader">
          <h2 style={{ margin: 0 }}>Price Calc. LME</h2>
          <div className="smallNote">({LME_UNIT_LABEL[lmeUnit]})</div>
        </div>

        <div className="form">
          <label>LME prices</label>
          <div className="row3">
            <div className="inputWithSuffix">
              <input
                type="number"
                value={lmePercent.pt}
                onChange={(e) => setLmePercent({ ...lmePercent, pt: e.target.value })}
                placeholder="Pt"
              />
              <span className="suffix">pt</span>
            </div>
            <div className="inputWithSuffix">
              <input
                type="number"
                value={lmePercent.pd}
                onChange={(e) => setLmePercent({ ...lmePercent, pd: e.target.value })}
                placeholder="Pd"
              />
              <span className="suffix">pd</span>
            </div>
            <div className="inputWithSuffix">
              <input
                type="number"
                value={lmePercent.rh}
                onChange={(e) => setLmePercent({ ...lmePercent, rh: e.target.value })}
                placeholder="Rh"
              />
              <span className="suffix">rh</span>
            </div>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="cardHeader">
          <h2 style={{ margin: 0 }}>Price Calc. % Deductions</h2>
          <div className="smallNote">(percent kept)</div>
        </div>

        <div className="form">
          <label>Recovery Rate %</label>
          <div className="row3">
            <div className="inputWithAffix">
              <input
                type="number"
                value={pctDisplay("pt")}
                onFocus={() => detachPct("pt")}
                onChange={(e) => {
                  detachPct("pt");
                  setPtPct(e.target.value);
                }}
                className={pctDetach.pt ? "isDetached" : ""}
              />
              <button
                type="button"
                className="affixBtn"
                onClick={() => reattachPct("pt")}
                title="Use saved"
                tabIndex={-1}
              >
                ↺
              </button>
              <span className="affixSuffix">pt</span>
            </div>

            <div className="inputWithAffix">
              <input
                type="number"
                value={pctDisplay("pd")}
                onFocus={() => detachPct("pd")}
                onChange={(e) => {
                  detachPct("pd");
                  setPdPct(e.target.value);
                }}
                className={pctDetach.pd ? "isDetached" : ""}
              />
              <button
                type="button"
                className="affixBtn"
                onClick={() => reattachPct("pd")}
                title="Use saved"
                tabIndex={-1}
              >
                ↺
              </button>
              <span className="affixSuffix">pd</span>
            </div>

            <div className="inputWithAffix">
              <input
                type="number"
                value={pctDisplay("rh")}
                onFocus={() => detachPct("rh")}
                onChange={(e) => {
                  detachPct("rh");
                  setRhPct(e.target.value);
                }}
                className={pctDetach.rh ? "isDetached" : ""}
              />
              <button
                type="button"
                className="affixBtn"
                onClick={() => reattachPct("rh")}
                title="Use saved"
                tabIndex={-1}
              >
                ↺
              </button>
              <span className="affixSuffix">rh</span>
            </div>
          </div>

          <div className="smallNote" style={{ marginTop: 8 }}>
            Applies only to Price Calc. (Percent) contract.
          </div>
        </div>
      </div>

      {/* Results grid */}
      <div className="gridFullWidth" style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}>
        <ResultCard title="Tech Result" model={resAdmin} />
        <ResultCard title="BASF Result" model={resBasf} />
        <ResultCard title="Price Calc. Result" model={resPercent} />
      </div>

      {toast ? (
        <div className={`toast ${toast.type === "err" ? "toastErr" : "toastOk"}`}>
          {toast.text}
        </div>
      ) : null}

{showCalcUnitModal ? (
  <div className="modalBackdrop" onMouseDown={() => setShowCalcUnitModal(false)}>
    <div className="modalCard" onMouseDown={(e) => e.stopPropagation()}>
      <div className="cardHeader" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Calculator units</h2>

        <button
          type="button"
          className="iconBtn btnGlass"
          onClick={() => setShowCalcUnitModal(false)}
          aria-label="Close"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>

      <div className="smallNote" style={{ marginBottom: 12 }}>
        These affect how inputs are interpreted.
      </div>

      {/* EXACT same layout style as AdminCalculator */}
      <div style={{ display: "grid", gap: 12 }}>
        {/* LME unit */}
        <div>
          <label style={{ display: "block", marginBottom: 6 }}>LME input unit</label>

          <div className="customSelect">
            <button
              ref={refLmeTrigger}
              type="button"
              className="customSelectTrigger"
              onClick={() => setLmeOpen((v) => !v)}
              onKeyDown={(e) => onSelectTriggerEnter(e, lmeOpen, setLmeOpen, refLmeOptOz)}
              aria-haspopup="listbox"
              aria-expanded={lmeOpen}
            >
              {lmeUnit === "per_oz" ? "per ounce" : "per gram"}
              <span className="chev">▾</span>
            </button>

            {lmeOpen && (
              <div className="customSelectMenu" role="listbox" aria-label="LME unit">
                <div
                  ref={refLmeOptOz}
                  tabIndex={0}
                  role="option"
                  aria-selected={lmeUnit === "per_oz"}
                  className={`customOption ${lmeUnit === "per_oz" ? "active" : ""}`}
                  onClick={() => {
                    setLmeUnit("per_oz");
                    setLmeOpen(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === "NumpadEnter") {
                      e.preventDefault();
                      setLmeUnit("per_oz");
                      setLmeOpen(false);
                      focusNext(refWeightTrigger);
                    }
                  }}
                >
                  per ounce
                </div>

                <div
                  tabIndex={0}
                  role="option"
                  aria-selected={lmeUnit === "per_g"}
                  className={`customOption ${lmeUnit === "per_g" ? "active" : ""}`}
                  onClick={() => {
                    setLmeUnit("per_g");
                    setLmeOpen(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === "NumpadEnter") {
                      e.preventDefault();
                      setLmeUnit("per_g");
                      setLmeOpen(false);
                      focusNext(refWeightTrigger);
                    }
                  }}
                >
                  per gram
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Weight unit */}
        <div>
          <label style={{ display: "block", marginBottom: 6 }}>Weight unit</label>

          <div className="customSelect">
            <button
              ref={refWeightTrigger}
              type="button"
              className="customSelectTrigger"
              onClick={() => setWeightOpen((v) => !v)}
              onKeyDown={(e) => onSelectTriggerEnter(e, weightOpen, setWeightOpen, refWeightOptG)}
              aria-haspopup="listbox"
              aria-expanded={weightOpen}
            >
              {weightUnit === "g" ? "grams" : "kg"}
              <span className="chev">▾</span>
            </button>

            {weightOpen && (
              <div className="customSelectMenu" role="listbox" aria-label="Weight unit">
                <div
                  ref={refWeightOptG}
                  tabIndex={0}
                  role="option"
                  aria-selected={weightUnit === "g"}
                  className={`customOption ${weightUnit === "g" ? "active" : ""}`}
                  onClick={() => {
                    setWeightUnit("g");
                    setWeightOpen(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === "NumpadEnter") {
                      e.preventDefault();
                      setWeightUnit("g");
                      setWeightOpen(false);
                    }
                  }}
                >
                  grams
                </div>

                <div
                  tabIndex={0}
                  role="option"
                  aria-selected={weightUnit === "kg"}
                  className={`customOption ${weightUnit === "kg" ? "active" : ""}`}
                  onClick={() => {
                    setWeightUnit("kg");
                    setWeightOpen(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === "NumpadEnter") {
                      e.preventDefault();
                      setWeightUnit("kg");
                      setWeightOpen(false);
                    }
                  }}
                >
                  kg
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 14 }}>
        <button type="button" className="btnGlass" onClick={() => setShowCalcUnitModal(false)}>
          Done
        </button>
      </div>
    </div>
  </div>
) : null}



    </div>
  );
}
